import {FormFieldItem, generateDropdownOptions, isCompanyUser} from '@common'
import {FormFieldType, ContractVariant, TranslationKey} from '@enums'
import {
  contractDropdownOptionsAtom,
  contractFormDataAtom,
  contractResourcesAtom,
  contractSetupDataAtom,
  contractSetupFormFieldErrorsAtom,
  formDataErrorsAtom,
  isFormDirtyAtom,
  lookupContractResourcesAtom,
} from '@state'
import {SetupFieldName} from '@state'

import {useAtom, useAtomValue, useSetAtom} from 'jotai'
import {useTranslation} from 'react-i18next'

import {UIKit} from '@components'

export interface ContractSetupFormProps {
  onRightBtnClick: () => void
}

export function useContractSetupFormModel(props: ContractSetupFormProps) {
  const {onRightBtnClick} = props

  const setIsFormDirty = useSetAtom(isFormDirtyAtom)

  //ATOMS
  const formData = useAtomValue(contractFormDataAtom)
  const contractResources = useAtomValue(contractResourcesAtom)
  const formFieldErrors = useAtomValue(formDataErrorsAtom)
  const [dropdownOptions, setDropdownOptions] = useAtom(contractDropdownOptionsAtom)
  const setContractSetupFormData = useSetAtom(contractSetupDataAtom)
  const lookupResource = useAtomValue(lookupContractResourcesAtom)
  const setContractSetupFormFieldErrors = useSetAtom(contractSetupFormFieldErrorsAtom)
  //END ATOMS

  const {t} = useTranslation()

  /**
   * A function handling Contract Type dropdown change
   * @param contractTypeId Contract Type ID
   */
  const onSelectContractType = (contractTypeId: string) => {
    onChangeSetupFormField('contractType', {value: contractTypeId, label: ''})
    setDropdownOptions(prev => ({
      ...prev,
      productOptions: generateDropdownOptions(
        contractResources.products
          .filter(item => item.companyId === formData.contractSetupData.company?.id)
          .map(item => ({...item, name: `${item.productNo} ${item.name}`})),
      ),
      accessoryOptions: contractResources.products
        .filter(item => item.companyId === formData.contractSetupData.company?.id)
        .map(item => ({
          productId: item.id,
          options: generateDropdownOptions(
            item.accessories.map(item => ({...item, name: `${item.productNo} ${item.name}`})),
          ),
        })),
    }))
    setIsFormDirty(true)
    onRightBtnClick()
  }

  /**
   * A function handling contract setup form fields change
   * @param fieldName Field name
   * @param item Dropdown item
   */
  const onChangeSetupFormField = (fieldName: string, item: UIKit.DropdownItem | null) => {
    const value = item?.value ?? null

    setContractSetupFormFieldErrors(prev => prev.filter(item => item !== fieldName))

    if (fieldName === 'contractType' && value !== null) {
      setContractSetupFormData(prev => ({
        ...prev,
        officialSignatory: formData.contractSetupData.dealer?.officialSignatories[0].name,
        length: contractResources.contractTypes.find(contractType => contractType.id === value)?.lengths?.[0] as number,
      }))
    }

    if (fieldName === 'dealer') {
      setDropdownOptions(prev => ({
        ...prev,
        contractTypeOptions: generateDropdownOptions(
          contractResources.contractTypes
            .filter(item => item.allowedDealers.some(dealer => dealer.id === value))
            .map(item => ({...item, name: `${item.name} ${item.type}`})),
        ),
      }))
    }
    if (fieldName === 'company') {
      setDropdownOptions(prev => ({
        ...prev,
        dealerOptions: generateDropdownOptions(contractResources.dealers.filter(item => item.companyId === value)),
      }))
      if (formData.contractSetupData.dealer?.companyId !== value) {
        setContractSetupFormData(prev => ({...prev, dealer: lookupResource(null)['dealer']}))
        setDropdownOptions(prev => ({
          ...prev,
          contractTypeOptions: [],
        }))
      }
    }
    setContractSetupFormData(prev => ({...prev, [fieldName]: lookupResource(value)[fieldName as SetupFieldName]}))
  }

  /**
   * Contract Setup form fields configuration
   */
  const contractSetupFormFields: FormFieldItem[] = [
    {
      fieldType: FormFieldType.RADIO_BUTTON_GROUP,
      onChange: (event: React.ChangeEvent<HTMLInputElement>, value: string) =>
        setContractSetupFormData(prev => ({...prev, type: value as ContractVariant})),
      itemsInColumn: 1,
      radioButtons: [
        {
          label: t(TranslationKey['Create contract']),
          value: ContractVariant.CREATION,
        },
        {
          label: t(TranslationKey['Create quote']),
          value: ContractVariant.QUOTE,
        },
      ],
      label: t(TranslationKey['What do you want to do']),
      value: formData.contractSetupData.type,
    },

    {
      fieldType: FormFieldType.SEARCHABLE_DROPDOWN,
      label: t(TranslationKey.Dealer),
      items: dropdownOptions.dealerOptions,
      name: 'dealer',
      onChange: (item: UIKit.DropdownItem | null) => onChangeSetupFormField('dealer', item),
      error: formFieldErrors.contractSetupFormFieldErrors.includes('dealer'),
      value: String(formData.contractSetupData.dealer?.id),
      disabled: isCompanyUser(),
    },
  ]

  return {contractSetupFormFields, dropdownOptions, onSelectContractType, t}
}
